/**
 * Copyright (c) Microsoft. All rights reserved.
 */

export type Tree = {
    children: readonly Tree[];
};

export const getAllTreeNodes = <TNode extends Tree>(tree: TNode): TNode[] => {
    const nodes: TNode[] = [];

    const addNode = (node: TNode) => {
        nodes.push(node);

        if (node.children && node.children.length) {
            node.children.forEach(addNode);
        }
    };

    addNode(tree);

    return nodes;
};

/**
 * Calculates the depth of the tree.
 */
export const calcTreeDepth = (tree: Tree): number => {
    let count = 0;
    let depth = 0;

    const calc = (node: Tree) => {
        count++;

        if (node.children && node.children.length) {
            node.children.forEach(calc);
        } else {
            depth = Math.max(depth, count);
        }

        count--;
    };

    calc(tree);

    return depth;
};

/**
 * Gets the level of the node in the tree. The level is 0 based.
 */
export const calcNodeLevel = <TNode extends Tree>(root: TNode, predicate?: (node: TNode) => boolean): number => {
    const getLevel = (n: TNode, depth: number): number => {
        if (predicate(n)) {
            return depth;
        }

        for (const child of n.children) {
            const d = getLevel(<TNode>child, depth + 1);
            if (d !== -1) {
                return d;
            }
        }

        return -1;
    };

    return getLevel(root, 0);
};

/**
 * Gets all the ancestor nodes and self for the given node.
 */
export const getAncestorsAndSelf = <TNode extends Tree>(tree: TNode, predicate: (node: TNode) => boolean): TNode[] => {
    const path: TNode[] = [];

    const getAncestorsHelper = (node: TNode): boolean => {
        if (!node) {
            return false;
        }

        path.push(node);

        if (predicate(node)) {
            return true;
        }

        for (const child of node.children) {
            if (getAncestorsHelper(<TNode>child)) {
                return true;
            }
        }

        path.splice(path.length - 1, 1);

        return false;
    };

    getAncestorsHelper(tree);

    return path;
};

export const findFirstNodes = <T extends Tree>(tree: T, predicate: (subTree: T) => boolean): T[] => {
    const result: T[] = [];

    const dfs = (subTree: T) => {
        if (!predicate(subTree)) {
            subTree.children.forEach(dfs);
        } else {
            result.push(subTree);
        }
    };

    dfs(tree);

    return result;
};

export const dfsTraverse = <T extends Tree>(tree: T, callback: (subTree: T, parent: T) => void) => {
    const dfs = (subTree: T, parent: T) => {
        callback(subTree, parent);

        subTree.children.forEach((node: T) => {
            dfs(node, subTree);
        });
    };

    dfs(tree, null);
};

export const bfsTraverse = <T extends Tree>(tree: T, callback: (subTree: T, parent: T) => void) => {
    const queue: { current: T; parent: T }[] = [{ current: tree, parent: null }];

    while (queue.length) {
        const { current, parent } = queue.shift();
        callback(current, parent);
        queue.push(...current.children.map(cur => ({ current: <T>cur, parent: current })));
    }
};
